import { formatInteger, trimLabel } from '@/domain/reporting/reporting-shared.formatters';
import { clamp, normalizeRatingAxisMax, toRounded } from '@/domain/reporting/reporting-shared.math';
import {
    type AspectRadarCoordinates,
    type AspectRadarGeometry,
    type AspectRadarGeometryAxis,
    type AspectRadarGeometryOptions,
    type AspectRadarPointInput,
    type RatingTimelineGeometry,
    type RatingTimelineGeometryPoint,
    type RatingTimelineGeometryOptions,
    type RatingTimelinePointInput,
    type TreemapLabelLayout,
    type TreemapLabelLayoutInput,
    type TreemapLabelLayoutOptions,
} from '@/domain/reporting/reporting-shared.types';

export const buildRatingTimelineGeometry = <T extends RatingTimelinePointInput>(
    points: T[],
    options: RatingTimelineGeometryOptions,
): RatingTimelineGeometry<T> | null => {
    if (!points.length) {
        return null;
    }

    const plotWidth = options.width - options.paddingLeft - options.paddingRight;
    const plotHeight = options.height - options.paddingTop - options.paddingBottom;
    const axisBottom = options.paddingTop + plotHeight;
    const axisRight = options.paddingLeft + plotWidth;

    const getX = (index: number): number => {
        if (points.length <= 1) {
            return options.paddingLeft + plotWidth / 2;
        }

        return options.paddingLeft + (index / (points.length - 1)) * plotWidth;
    };

    const getY = (value: number): number => {
        const normalizedValue = clamp(value, options.axisMin, options.axisMax);
        return (
            options.paddingTop +
            ((options.axisMax - normalizedValue) / (options.axisMax - options.axisMin)) * plotHeight
        );
    };

    const normalizedPoints: RatingTimelineGeometryPoint<T>[] = points.map((point, index) => ({
        ...point,
        x: getX(index),
        y: getY(point.averageRating),
    }));

    return {
        width: options.width,
        height: options.height,
        plotWidth,
        plotHeight,
        axisBottom,
        axisRight,
        yTicks: (
            options.tickValues ||
            new Array(options.axisMax - options.axisMin + 1).fill(false).map((_, index) => options.axisMin + index)
        ).map((value) => ({
            value,
            y: getY(value),
        })),
        points: normalizedPoints,
    };
};

export const buildAspectRadarGeometry = <T extends AspectRadarPointInput>(
    points: T[],
    options: AspectRadarGeometryOptions,
): AspectRadarGeometry<T> | null => {
    if (!points.length) {
        return null;
    }

    const toCoordinates = (angle: number, radius: number): AspectRadarCoordinates => ({
        x: options.centerX + Math.cos(angle) * radius,
        y: options.centerY + Math.sin(angle) * radius,
    });

    const maxIntensity = normalizeRatingAxisMax(
        Math.max(
            ...points.map((point) => Math.max(point.positiveAverageIntensity, point.negativeAverageIntensity, 0)),
            0,
        ),
    );

    const axis: AspectRadarGeometryAxis<T>[] = points.map((point, index) => {
        const angle = -Math.PI / 2 + (Math.PI * 2 * index) / points.length;
        const axisEnd = toCoordinates(angle, options.radius);
        const positiveRadius = (Math.max(0, point.positiveAverageIntensity) / maxIntensity) * options.radius;
        const negativeRadius = (Math.max(0, point.negativeAverageIntensity) / maxIntensity) * options.radius;

        return {
            point,
            angle,
            axisEnd,
            positiveMarker: toCoordinates(angle, positiveRadius),
            negativeMarker: toCoordinates(angle, negativeRadius),
        };
    });

    const gridLevels =
        Number.isFinite(options.gridLevels) && options.gridLevels > 0 ? Math.floor(options.gridLevels) : 1;

    const levels = new Array(gridLevels).fill(false).map((_, levelIndex) => {
        const level = levelIndex + 1;
        const radius = (options.radius / gridLevels) * level;
        const polygon = axis
            .map(({ angle }) => {
                const coords = toCoordinates(angle, radius);
                return `${toRounded(coords.x)},${toRounded(coords.y)}`;
            })
            .join(' ');

        return {
            polygon,
        };
    });

    const positivePolygon = axis
        .map(({ positiveMarker }) => `${toRounded(positiveMarker.x)},${toRounded(positiveMarker.y)}`)
        .join(' ');
    const negativePolygon = axis
        .map(({ negativeMarker }) => `${toRounded(negativeMarker.x)},${toRounded(negativeMarker.y)}`)
        .join(' ');

    return {
        width: options.width,
        height: options.height,
        centerX: options.centerX,
        centerY: options.centerY,
        axis,
        levels,
        positivePolygon,
        negativePolygon,
    };
};

export const buildTreemapLabelLayout = (
    input: TreemapLabelLayoutInput,
    options: TreemapLabelLayoutOptions,
): TreemapLabelLayout => {
    const labelWithValue = `${input.label} (${formatInteger(input.value)})`;
    const area = input.width * input.height;

    const meetsArea = typeof options.minArea === 'number' ? area >= options.minArea : true;
    const meetsWidth = typeof options.minWidth === 'number' ? input.width >= options.minWidth : true;
    const meetsHeight = typeof options.minHeight === 'number' ? input.height >= options.minHeight : true;
    const canShowLabel = meetsArea && meetsWidth && meetsHeight;

    const estimatedLabelWidth = Math.max(
        options.minEstimatedLabelWidth,
        labelWithValue.length * options.charWidthMultiplier,
    );
    const fitsHorizontally = input.width >= estimatedLabelWidth;
    const fitsVertically = input.height >= options.minVerticalFitHeight;

    return {
        labelWithValue,
        canShowLabel,
        fitsHorizontally,
        fitsVertically,
        label: canShowLabel
            ? trimLabel(
                  labelWithValue,
                  fitsHorizontally
                      ? options.maxLabelLengthWhenHorizontalFit
                      : options.maxLabelLengthWhenNotHorizontalFit,
              )
            : null,
    };
};
